import { useQuery } from "@tanstack/react-query";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { useState } from "react";
import { Link } from "wouter";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { SkeletonText } from "@/components/ui/luxury-skeleton";
import { PageHero } from "@/components/layout/page-hero";
import { BlogPostCard } from "@/components/blog/blog-post-card";
import { categoryVisual, blogImg } from "@/lib/blog-visuals";
import { readingMinutes } from "@/components/blog/reading-time";
import { cleanExcerpt } from "@/components/blog/excerpt";
import { useBookCta } from "@/hooks/use-book-href";
import { useSEO, useSEOSettings, DEFAULT_SEO_SETTINGS, generateBreadcrumbSchema, generateItemListSchema, generateKnowledgeGraphSchema, generateSpeakableSchema, LOCATION_KEYWORDS } from "@/lib/seo-utils";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { ArrowRight } from "lucide-react";
import type { BlogCategory, BlogPostWithCategory } from "@shared/schema";

function CategorySection({
  category,
  posts
}: {
  category: BlogCategory;
  posts: BlogPostWithCategory[];
}) {
  if (posts.length === 0) return null;
  const visual = categoryVisual(category.slug);
  const Icon = visual.icon;
  const [lead, ...rest] = posts;
  const leadMinutes = readingMinutes(lead);

  return (
    <section className="mb-20" data-testid={`section-category-${category.slug}`}>
      {/* The category as a photo tile with its lead guide beside it. */}
      <div className="grid overflow-hidden border border-sand-deep md:grid-cols-[1.05fr_1fr]">
        <Link href={`/blog/category/${category.slug}`} className="group relative block min-h-[240px] md:min-h-[360px]" data-testid={`link-category-header-${category.slug}`}>
          <img
            src={blogImg(visual.image, 1000)}
            srcSet={`${blogImg(visual.image, 640)} 640w, ${blogImg(visual.image, 1000)} 1000w, ${blogImg(visual.image, 1400)} 1400w`}
            sizes="(min-width: 768px) 50vw, 100vw"
            alt={visual.alt}
            className="h-[240px] w-full object-cover"
            loading="lazy"
            decoding="async"
          />

          <div className="relative p-6 md:p-8 text-forest bg-shell">
            <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1 font-sans text-[10px] uppercase tracking-[0.28em] backdrop-blur-sm">
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
              {posts.length} {posts.length === 1 ? "guide" : "guides"}
            </span>
            <h2 className="font-serif text-[clamp(26px,3.4vw,38px)] font-normal leading-tight tracking-tight">
              {category.name}
            </h2>
            <p className="mt-2 max-w-[40ch] font-sans text-[14px] font-light leading-relaxed text-ink-soft">
              {category.description || visual.blurb}
            </p>
          </div>
        </Link>

        <article className="group relative flex flex-col bg-sand p-8 md:p-10" data-testid={`card-blog-post-${lead.id}`}>
          <span className="absolute left-0 top-0 h-[3px] w-10 transition-all duration-500 group-hover:w-full" style={{ backgroundColor: visual.accent }} aria-hidden="true" />
          <span className="mb-4 font-sans text-[10px] uppercase tracking-[0.28em]" style={{ color: visual.accent }}>Start here</span>
          <h3 className="mb-4 font-serif text-luxury-2xl font-normal leading-tight tracking-tight text-forest">
            <Link href={`/blog/${lead.slug}`} className="transition-colors after:absolute after:inset-0 after:content-[''] group-hover:text-ink" data-testid={`link-post-${lead.slug}`}>
              {lead.title}
            </Link>
          </h3>
          <p className="mb-8 flex-grow font-sans text-[15px] font-light leading-relaxed text-ink-soft" data-testid={`text-excerpt-${lead.id}`}>
            {cleanExcerpt(lead.title, lead.excerpt, 220)}
          </p>
          <div className="mt-auto flex items-center justify-between border-t border-sand-deep pt-6">
            <span className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood transition-colors group-hover:text-forest">
              Read guide
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            {leadMinutes !== null && (
              <span className="font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood">{leadMinutes} min</span>
            )}
          </div>
        </article>
      </div>

      {rest.length > 0 && (
        <div className="grid gap-px border border-t-0 border-sand-deep bg-sand-deep sm:grid-cols-2 lg:grid-cols-3">
          {rest.slice(0, 3).map((post) => (
            <BlogPostCard key={post.id} post={post} showCategory={false} compact />
          ))}
        </div>
      )}

      {posts.length > 4 && (
        <div className="mt-6 text-right">
          <Link
            href={`/blog/category/${category.slug}`}
            className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:text-ink"
            data-testid={`link-view-all-${category.slug}`}
          >
            All {posts.length} {category.name.toLowerCase()} guides
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
}

export default function BlogIndexPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const bookCta = useBookCta();
  const { seoSettings } = useSEOSettings();

  const { data: categories, isLoading: categoriesLoading } = useQuery<BlogCategory[]>({
    queryKey: ['/api/blog/categories'],
  });

  const { data: allPosts, isLoading: postsLoading } = useQuery<BlogPostWithCategory[]>({
    queryKey: ['/api/blog/posts'],
    queryFn: async () => {
      const response = await fetch('/api/blog/posts?limit=100');
      if (!response.ok) throw new Error('Failed to fetch posts');
      return response.json();
    },
  });

  const totalPosts = allPosts?.length || 0;

  const seoHead = useSEO({
    pageType: "blog",
    title: DEFAULT_SEO_SETTINGS.page_titles.blog,
    description: `Complete Oahu travel guide with ${totalPosts}+ expert articles. Best beaches, hiking trails, snorkeling spots, Hawaiian food, and insider tips for your Hawaii vacation.`,
    keywords: `oahu travel guide, hawaii vacation tips, ${LOCATION_KEYWORDS.activities}, oahu beaches, hawaii hiking trails, snorkeling oahu, hawaiian food guide, saturday farmers market oahu, trip planning hawaii, things to do oahu`,
    ogType: "website",
    canonical: "https://vacationrentaloahu.co/blog",
    twitterCard: "summary_large_image",
    structuredData: seoSettings && categories ? [
      generateKnowledgeGraphSchema({ seoSettings }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Travel Guide", url: "/blog" }
      ]),
      generateItemListSchema(
        categories.map((cat, index) => ({
          name: cat.name,
          url: `/blog/category/${cat.slug}`,
          description: cat.description || undefined,
          position: index + 1
        })),
        "Oahu Travel Guide Categories"
      ),
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Oahu Travel Guide",
        "description": "Comprehensive travel resource for Oahu, Hawaii with expert guides on beaches, hiking, snorkeling, food, and trip planning.",
        "url": "https://vacationrentaloahu.co/blog",
        "publisher": {
          "@type": "Organization",
          "name": "The Kualoa Beach House",
          "url": "https://vacationrentaloahu.co"
        },
        "blogPost": allPosts?.slice(0, 10).map(post => ({
          "@type": "BlogPosting",
          "headline": post.title,
          "url": `https://vacationrentaloahu.co/blog/${post.slug}`,
          "datePublished": post.published_at || post.created_at,
          "description": post.excerpt
        })) || []
      },
      generateSpeakableSchema(["h1", ".hub-intro"])
    ] : []
  });

  const isLoading = categoriesLoading || postsLoading;

  const getPostsForCategory = (categoryId: string): BlogPostWithCategory[] => {
    const posts = allPosts?.filter(post => post.category_id === categoryId) || [];
    if (!searchQuery.trim()) return posts;
    const q = searchQuery.toLowerCase();
    return posts.filter(
      (p) => p.title.toLowerCase().includes(q) || p.excerpt?.toLowerCase().includes(q) || p.slug.includes(q)
    );
  };

  const visibleCategories = categories?.filter(
    (cat) => getPostsForCategory(cat.id).length > 0
  );

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0">
      <Header />

      <main id="main-content" className="pb-16">
        {/* Hero Section with Video Background */}
        <PageHero
          title="Oahu Travel Guide"
          subtitle="Expert tips and local insights for exploring beaches, hiking trails, food spots, and hidden gems across Oahu, Hawaii."
          videoSrc={wavesVideo}
          posterSrc={wavesPoster}
          imageAlt="Tranquil Oahu beachfront with crystal clear waters - explore our travel guide"
        />

        <div className="container mx-auto px-4 max-w-5xl pt-6">
          <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Travel Guide", url: "/blog" }]} />
        </div>

        {/* Hub intro + conversion CTAs */}
        <section className="py-16 container mx-auto px-4 max-w-5xl hub-intro">
          <p className="text-luxury-lg text-ink-soft font-light leading-relaxed mb-8">
            The Oahu Travel Guide is your hub for beaches, hiking, snorkeling, food, and trip planning, written from our home base in Ka'a'awa on the windward coast.
            Use these guides to plan your trip, then{" "}
            <Link href={bookCta.choosing ? "/properties" : KUALOA_STAY_PATH} className="text-ink underline decoration-sand-deep underline-offset-4 hover:text-ink/70">{bookCta.choosing ? "explore our homes" : "explore our beach house"}</Link> or{" "}
            <Link href={bookCta.href} className="text-ink underline decoration-sand-deep underline-offset-4 hover:text-ink/70">{bookCta.choosing ? "choose a home" : "check availability"}</Link>.
          </p>
          <div className="mb-8">
            <input
              type="search"
              placeholder="Search travel guides..."
              aria-label="Search travel guides"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full max-w-md border border-sand-deep bg-shell px-4 py-3 font-sans text-[15px] font-light text-ink transition-colors placeholder:text-driftwood focus:border-forest focus:outline-none"
              data-testid="blog-search-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link
              href={bookCta.href}
              data-primary-book-cta
              className="inline-flex items-center justify-center bg-sun px-8 py-4 font-sans tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover"
            >
              {bookCta.choosing ? "Choose a home" : "Check availability"}
            </Link>
            <Link
              href={bookCta.choosing ? "/properties" : KUALOA_STAY_PATH}
              className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
            >
              {bookCta.choosing ? "View our homes" : "View the property"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              href="/kaaawa"
              className="inline-flex items-center gap-2 border-b border-transparent pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-driftwood transition-colors hover:border-sand-deep hover:text-forest"
            >
              Ka'a'awa area guide
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </section>

        {/* Category Sections */}
        <section className="py-12 container mx-auto px-4 max-w-5xl">
          {isLoading ? (
            <div className="space-y-20">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <div className="mb-8 flex items-baseline justify-between border-b border-sand-deep pb-5">
                    <SkeletonText lines={1} className="h-7 w-56" />
                    <SkeletonText lines={1} className="h-3 w-20" />
                  </div>
                  <div className="grid gap-px border border-sand-deep bg-sand-deep md:grid-cols-2">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div key={j} className="bg-shell p-8 md:p-10">
                        <SkeletonText lines={1} className="mb-4 h-6 w-3/4" />
                        <SkeletonText lines={2} className="mb-8 w-full" />
                        <div className="flex justify-between border-t border-sand-deep pt-6">
                          <SkeletonText lines={1} className="h-3 w-24" />
                          <SkeletonText lines={1} className="h-3 w-12" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : visibleCategories && visibleCategories.length > 0 ? (
            <>
              {visibleCategories.map((category) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  posts={getPostsForCategory(category.id)}
                />
              ))}
            </>
          ) : searchQuery.trim() ? (
            <div className="border border-sand-deep bg-sand/50 px-6 py-16 text-center">
              <p className="font-sans text-[15px] font-light text-ink-soft">
                No articles match &ldquo;{searchQuery}&rdquo;. Try a different search.
              </p>
            </div>
          ) : (
            <div className="border border-sand-deep bg-sand/50 px-6 py-16 text-center">
              <h3 className="mb-3 font-serif text-luxury-xl font-normal tracking-tight text-forest">Coming Soon</h3>
              <p className="mx-auto max-w-[46ch] font-sans text-[15px] font-light leading-relaxed text-ink-soft">
                Our travel guides are being prepared. Check back soon for insider tips on exploring Oahu.
              </p>
            </div>
          )}
        </section>

        {/* CTA Section */}
        <section className="container mx-auto max-w-5xl px-6 pt-8">
          <div className="border-t border-sand-deep py-16 text-center md:py-20">
            <span className="mb-5 inline-block font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
              Ka'a'awa &middot; Windward Oahu
            </span>
            <h2 className="mx-auto mb-5 max-w-[18ch] font-serif text-[clamp(26px,4vw,42px)] font-normal leading-tight tracking-tight text-forest">
              Ready to experience Oahu?
            </h2>
            <p className="mx-auto mb-10 max-w-[52ch] font-sans text-[16px] font-light leading-relaxed text-ink-soft">
              {bookCta.choosing
                ? "Stay at one of our beachfront homes in Ka'a'awa, minutes from Kualoa Ranch."
                : "Stay at our beachfront house in Ka'a'awa, just steps from Kualoa Ranch."}
            </p>
            <Link
              href={bookCta.href}
              data-primary-book-cta
              className="inline-flex items-center justify-center bg-sun px-8 py-4 font-sans tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover"
              data-testid="button-book-now"
            >
              {bookCta.choosing ? "Choose a home" : "Check availability"}
            </Link>
          </div>
        </section>
      </main>

      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
